// src/pages/authn/AuthnDetailPage.tsx
// OAuth2Client detail view.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useAuthn, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, CardFooter, Alert, Badge, ErrorAlert,
} from '@react/components';
import type { OAuth2Client } from '@core/types/authn';

export const AuthnDetailPage = observer(() => {
  const authnStore = useAuthn();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [newSecret, setNewSecret] = useState('');

  useEffect(() => { if (id) authnStore.GetOAuth2Client(id); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const client = authnStore.currentItem as OAuth2Client | null;
  if (authnStore.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  if (!client) return <Alert variant="error">{t('common.notFound')}</Alert>;

  const handleDelete = async () => {
    if (!confirm(t('authn.deleteConfirm'))) return;
    await authnStore.DeleteOAuth2Client(client.entity?.id ?? '');
    navigate('/authns');
  };

  const handleRotateSecret = async () => {
    if (!confirm(t('authn.rotateSecretConfirm'))) return;
    try {
      const resp = await authnStore.RotateOAuth2ClientSecret(client.entity?.id ?? '', {});
      setNewSecret((resp as never as { client_secret: string }).client_secret ?? '');
    } catch {
      // error handled by store
    }
  };

  const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm text-foreground sm:col-span-2 sm:mt-0 ${mono ? 'font-mono' : ''}`}>{value || t('common.notSet')}</dd>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/authns" className="text-sm text-primary hover:text-primary/80">{t('common.backToList')}</Link>
          <h1 className="text-2xl font-semibold text-foreground mt-1">{client.client_name}</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/authns/${client.entity?.id}/edit`}><Button variant="secondary">{t('common.edit')}</Button></Link>
          <Button variant="danger" onClick={handleDelete}>{t('common.delete')}</Button>
        </div>
      </div>

      {authnStore.error ? <ErrorAlert error={authnStore.error} dismissible onDismiss={() => authnStore.clearError()} /> : null}

      {newSecret && (
        <Card>
          <CardHeader><CardTitle>{t('authn.newClientSecret')}</CardTitle></CardHeader>
          <CardContent>
            <Alert variant="warning" className="mb-3">{t('authn.saveClientSecret')}</Alert>
            <code className="block p-3 bg-muted rounded-lg font-mono text-sm select-all break-all">{newSecret}</code>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>{t('authn.clientDetails')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <Field label={t('common.id')} value={client.entity?.id} mono />
            <Field label={t('authn.clientId')} value={client.client_id} mono />
            <Field label={t('authn.clientName')} value={client.client_name} />
            <Field label={t('authn.clientType')} value={client.client_type} />
            <Field label={t('common.description')} value={client.description} />
            <Field label={t('authn.enabled')} value={<Badge variant={client.enabled ? 'success' : 'secondary'}>{client.enabled ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('authn.redirectUris')} value={(client.redirect_uris?.items ?? []).join(', ')} mono />
            <Field label={t('authn.grantTypes')} value={(client.allowed_grant_types?.items ?? []).join(', ')} />
            <Field label={t('authn.scopes')} value={(client.allowed_scopes?.items ?? []).join(', ')} />
            <Field label={t('authn.responseTypes')} value={(client.allowed_response_types?.items ?? []).join(', ')} />
            <Field label={t('authn.logoUri')} value={client.logo_uri} mono />
            <Field label={t('authn.policyUri')} value={client.policy_uri} mono />
            <Field label={t('authn.tosUri')} value={client.tos_uri} mono />
            <Field label={t('authn.contacts')} value={(client.contacts?.items ?? []).join(', ')} />
            <Field label={t('common.createdAt')} value={client.created_at} />
            <Field label={t('common.updatedAt')} value={client.updated_at} />
          </dl>
        </CardContent>
        {client.client_type === 'confidential' && (
          <CardFooter>
            <Button variant="secondary" onClick={handleRotateSecret}>{t('authn.rotateSecret')}</Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
});

export default AuthnDetailPage;
